import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Bottom sheet – vyjede zespodu, protože na spodní hranu palec dosáhne,
 * na rozdíl od dialogu uprostřed obrazovky (spec 6).
 *
 * Řeší přístupnost, kterou by jinak každý dialog musel řešit sám:
 * `role="dialog"` + `aria-modal`, fokus dovnitř při otevření, past na Tab,
 * zavření Escapem a klikem na pozadí, návrat fokusu tam, odkud se otevřel.
 */
interface Props {
  title: string;
  /** Druhý řádek v hlavičce – číslo zásilky, kód, stav. */
  meta?: string;
  onClose: () => void;
  children: ReactNode;
}

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export function Sheet({ title, meta, onClose, children }: Props) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    restoreRef.current = document.activeElement as HTMLElement | null;
    const el = sheetRef.current;
    if (!el) return;

    // Autofocus jen na krátký textový vstup (číslo objednávky, váha).
    // Do poznámky v hlášení fokus schválně NEskáče: na mobilu by vyjela
    // klávesnice a překryla výběr důvodu, který má skladník potvrdit první.
    const first = el.querySelector<HTMLElement>('input.qinput') ?? el;
    first.focus({ preventScroll: true });

    return () => restoreRef.current?.focus?.({ preventScroll: true });
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const el = sheetRef.current;
      if (!el) return;
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  return (
    <div className="sheet-host" onKeyDown={onKeyDown}>
      <button className="scrim" type="button" aria-label="Zavřít" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} ref={sheetRef} tabIndex={-1}>
        <div className="grab" />
        <h4>{title}</h4>
        {meta && <div className="meta">{meta}</div>}
        {children}
      </div>
    </div>
  );
}
